import clsxm from "@/lib/clsxm";
import {useCallback, useRef, type FC} from "react";
import ReactGoogleAutocomplete, {type ReactGoogleAutocompleteInputProps} from "react-google-autocomplete";
import {useTranslation} from "react-i18next";

type DynamicLocationFieldProps = ReactGoogleAutocompleteInputProps & {
  inputClassName?: string;
  isEstablishmentEnabled?: boolean;
  labelName?: string;
  labelClassName?: string;
  onPick: (data: TDynamicLocationData) => void;
};

type TDynamicLocationData = {
  placeId: string;
  description?: string;
  streetNumber: string | null;
  street: string | null;
  postalCode: string | null;
  city: string | null;
  country: string | null;
  establishment: string | null;
};

type TGoogleAddressComponent = {
  long_name: string;
  short_name: string;
  types: string[];
};

const DynamicLocationField: FC<DynamicLocationFieldProps> = ({
  inputClassName,
  isEstablishmentEnabled,
  labelName,
  labelClassName,
  onPick,
  ...props
}) => {
  const {t, i18n} = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const getAddressComponent = useCallback(
    (addressComponents: TGoogleAddressComponent[], types: string[]) =>
      addressComponents?.find(component => types.some(type => component.types.includes(type)))?.long_name ?? null,
    [],
  );

  return (
    <>
      {labelName && (
        <label className={clsxm("text-neutral-black text-sm font-bold", labelClassName)}>{t(labelName)}</label>
      )}
      <ReactGoogleAutocomplete
        {...props}
        language={i18n.resolvedLanguage === "ger" ? "de" : i18n.resolvedLanguage}
        className={inputClassName}
        ref={inputRef}
        onFocus={e => props.onFocus?.(e)}
        apiKey={import.meta.env.VITE_APP_GOOGLE_MAPS_API_KEY as string}
        options={{
          types: isEstablishmentEnabled ? ["establishment", "geocode"] : ["address"],
        }}
        onPlaceSelected={place => {
          const addressComponents = place.address_components!;
          const placeId = place.place_id!;
          const description = place.formatted_address;
          const streetNumber = getAddressComponent(addressComponents, ["street_number"]);
          const street = getAddressComponent(addressComponents, ["route"]);
          const postalCode = getAddressComponent(addressComponents, ["postal_code"]);
          const city = getAddressComponent(addressComponents, ["locality", "postal_town"]);
          const country = getAddressComponent(addressComponents, ["country"]);
          let placesService = null;

          if (window.google?.maps?.places?.PlacesService) {
            placesService = new window.google.maps.places.PlacesService(document.createElement("div"));
          }

          if (placesService) {
            placesService.getDetails({placeId}, resp => {
              const types = resp?.types ?? [];

              let establishment = null;

              if (resp?.name && types.includes("establishment")) {
                establishment = resp?.name;
              }

              const locationData: TDynamicLocationData = {
                placeId,
                description,
                streetNumber,
                street,
                postalCode,
                city,
                country,
                establishment,
              };

              onPick(locationData);

              if (inputRef.current && description) {
                const value = establishment ? `${establishment}, ${description}` : description;

                inputRef.current.value = value;
              }
            });
          }
        }}
      />
    </>
  );
};

export default DynamicLocationField;
